import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Search } from "lucide-react";
import { CBHLogo } from "./CBHLogo";
import { useCart } from "../context/CartContext";

const navLinks = [
  { to: "/", label: "Home", short: "Home", end: true },
  { to: "/books", label: "Books", short: "Books" },
  { to: "/publish", label: "Publish With Us", short: "Publish" },
  { to: "/districts", label: "Districts & Diaspora", short: "Districts" },
  { to: "/studio", label: "Conlang Studio", short: "Studio" },
  { to: "/ylgf", label: "YLGF", short: "YLGF" },
  { to: "/about", label: "About", short: "About" },
  { to: "/contact", label: "Contact", short: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { totalQty } = useCart();
  const location = useLocation();

  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 bg-ivory/85 backdrop-blur-xl border-b border-crimson/10"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link">
          <CBHLogo />
        </Link>

        <nav className="hidden xl:flex items-center gap-7 2xl:gap-10" data-testid="primary-nav">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.18em] whitespace-nowrap link-underline transition-colors duration-300 ${
                  isActive ? "text-crimson" : "text-ink hover:text-crimson"
                }`
              }
            >
              <span className="2xl:hidden">{l.short}</span>
              <span className="hidden 2xl:inline">{l.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/books"
            aria-label="Search catalogue"
            data-testid="header-search-link"
            className="hidden md:inline-flex items-center justify-center w-10 h-10 text-ink hover:text-crimson transition-colors duration-300"
          >
            <Search strokeWidth={1.25} className="w-5 h-5" />
          </Link>
          <Link
            to="/cart"
            aria-label="Cart"
            data-testid="header-cart-link"
            className="relative inline-flex items-center justify-center w-10 h-10 text-ink hover:text-crimson transition-colors duration-300"
          >
            <ShoppingBag strokeWidth={1.25} className="w-5 h-5" />
            {totalQty > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-ivory text-[10px] font-semibold flex items-center justify-center"
              >
                {totalQty}
              </span>
            )}
          </Link>
          <button
            className="xl:hidden inline-flex items-center justify-center w-10 h-10 text-ink"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.25} className="w-6 h-6" /> : <Menu strokeWidth={1.25} className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="xl:hidden bg-ivory border-t border-crimson/10"
          data-testid="mobile-nav"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.22em] ${
                    isActive ? "text-crimson" : "text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    className="bg-ink text-ivory/85 mt-32 grain-overlay"
    data-testid="site-footer"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <CBHLogo variant="light" />
          <p className="mt-8 max-w-md text-ivory/70 font-editorial italic text-lg leading-relaxed">
            &ldquo;Where ideas become institutions.&rdquo;
          </p>
          <p className="mt-6 text-sm text-ivory/60 max-w-md leading-relaxed">
            A modern publishing house building an idea-to-impact ecosystem — books, biographies,
            wisdom, and civic literature from Bharat, for the world.
          </p>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-[0.28em] text-gold-light mb-5">Catalogue</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/books" className="hover:text-gold-light transition-colors">All Books</Link></li>
            <li><Link to="/books?imprint=Bharat%20Icon%20Series" className="hover:text-gold-light transition-colors">Bharat Icon Series</Link></li>
            <li><Link to="/studio" className="hover:text-gold-light transition-colors">Conlang Studio</Link></li>
            <li><Link to="/districts" className="hover:text-gold-light transition-colors">Districts & Diaspora</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-[0.28em] text-gold-light mb-5">The House</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/publish" className="hover:text-gold-light transition-colors">Publish With Us</Link></li>
            <li><Link to="/ylgf" className="hover:text-gold-light transition-colors">YLGF Fellowship</Link></li>
            <li><Link to="/about" className="hover:text-gold-light transition-colors">About CBH</Link></li>
            <li><Link to="/contact" className="hover:text-gold-light transition-colors">Contact & Rights</Link></li>
            <li><Link to="/cart" className="hover:text-gold-light transition-colors">Cart & Checkout</Link></li>
          </ul>
        </div>
      </div>

      <hr className="mt-16 border-ivory/10" />

      <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <p className="text-xs text-ivory/55 tracking-[0.14em] uppercase">
          Conlang Book House (CBH) - Powered by Conlang International
        </p>
        <p className="text-xs text-ivory/40">
          © {new Date().getFullYear()} Conlang Book House. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

export const Layout = () => (
  <div className="min-h-screen flex flex-col bg-ivory">
    <Header />
    <main className="flex-1" data-testid="page-main">
      <Outlet />
    </main>
    <Footer />
  </div>
);

export default Layout;
